import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Chip,
  Stack,
  Skeleton
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  LocalOffer as LocalOfferIcon,
  Speed as SpeedIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import api from '../utils/api';
import analytics from '../services/analytics';
import { useComponentTracking } from '../hooks/useAnalytics';
import { useCouponStatus, formatCouponDisplay } from '../hooks/useCouponStatus';

const ReferralLanding = () => {
  const { referralToken } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [referralData, setReferralData] = useState(null);
  
  // Track component mount/unmount
  useComponentTracking('ReferralLanding');
  
  // Fetch coupon status
  const { couponData, loading: couponLoading } = useCouponStatus('ALPHA90');

  useEffect(() => {
    validateReferralToken();
  }, [referralToken]);

  const validateReferralToken = async () => {
    try {
      const response = await api.get(`/referral/validate/${referralToken}`);
      if (response.data.valid) {
        setReferralData(response.data);
        // Store referral token in session storage for registration
        sessionStorage.setItem('referralToken', referralToken);
        sessionStorage.setItem('referrerName', response.data.referrerName);
        sessionStorage.setItem('referrerId', response.data.referrerId);
        
        // Track valid referral visit
        analytics.trackAlphaReferralVisited(
          referralToken,
          response.data.referrerId,
          true,
          null
        );
      } else {
        setError('This referral link is no longer valid. It may have expired or reached its usage limit.');
        
        // Track invalid referral visit
        analytics.trackAlphaReferralVisited(
          referralToken,
          response.data.referrerId || null,
          false,
          response.data.error_reason || 'invalid_token'
        );
      }
    } catch (err) {
      console.error('Referral validation error:', err);
      setError('Invalid referral link. Please check the link and try again.');
      
      // Track error referral visit
      analytics.trackAlphaReferralVisited(
        referralToken,
        null,
        false,
        'validation_error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClick = () => {
    // Track referral join click
    analytics.track('referral_join_clicked', {
      referral_token: referralToken,
      referrer_id: referralData?.referrerId || null,
      referrer_name: referralData?.referrerName || null
    });
    
    // Navigate to registration with referral data preserved in session storage
    navigate('/register');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          fullWidth 
          variant="contained" 
          onClick={() => navigate('/register')}
          sx={{ mt: 2 }}
        >
          Sign Up Without Referral
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        {/* Welcome Message */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            🎉 You're Invited to the Alpha Program!
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
            {referralData?.referrerName} has invited you to join our exclusive AI examr alpha testing program
          </Typography>
          <Chip 
            label="ALPHA TESTER INVITATION" 
            color="secondary" 
            sx={{ 
              fontSize: '1rem',
              py: 2,
              px: 3,
              background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        </Box>

        {/* Benefits Section */}
        <Card sx={{ mb: 4, bgcolor: 'background.default' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon color="primary" />
              Your Exclusive Alpha Benefits
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="h6">5 FREE Trial Exams</Typography>}
                  secondary="Regular users get only 2 trial exams - you get 5!"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LocalOfferIcon color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6">90% OFF Subscriptions</Typography>
                      <Chip label="FOUNDER'S DEAL" color="error" size="small" />
                      {couponLoading ? (
                        <Skeleton width={100} height={20} />
                      ) : couponData && couponData.valid && (
                        <Typography variant="body2" color="text.secondary">
                          - {formatCouponDisplay(couponData.max_uses, couponData.current_uses)}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    couponData && !couponData.is_available 
                      ? "All Founder's Deal spots have been claimed"
                      : "Founder's Deal discount automatically applied at checkout"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SpeedIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="h6">Early Access</Typography>}
                  secondary="Be the first to try new features before public release"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SchoolIcon color="secondary" />
                </ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="h6">Direct Feedback Channel</Typography>}
                  secondary="Your input shapes the future of our platform"
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Box mb={4}>
          <Typography variant="h5" gutterBottom>
            How It Works
          </Typography>
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip label="1" color="primary" />
              <Typography>Click "Join Alpha Program" below to create your account</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip label="2" color="primary" />
              <Typography>Explore the platform with your 5 free trial exams</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip label="3" color="primary" />
              <Typography>When ready, your Founder's Deal discount will be automatically applied</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip label="4" color="primary" />
              <Typography>Share your own referral link to invite up to 2 friends!</Typography>
            </Box>
          </Stack>
        </Box>

        {/* CTA Button */}
        <Box textAlign="center">
          <Button
            variant="contained"
            size="large"
            onClick={handleJoinClick}
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #8e44ad 0%, #7a3a9a 100%)',
              }
            }}
          >
            Join Alpha Program Now
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {couponData && couponData.is_available ? (
              <>Only {couponData.remaining_uses} Founder's Deal {couponData.remaining_uses === 1 ? 'spot' : 'spots'} remaining • Invitation expires in 48 hours</>
            ) : couponData && !couponData.is_available ? (
              <>All Founder's Deal spots taken • Regular pricing applies</>
            ) : (
              <>Limited spots available • Invitation expires in 48 hours</>
            )}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default ReferralLanding;